import { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { registerUser } from '../api/authApi';
import '../styles/Auth.css';

const Register = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobile: false,
    password: false,
    confirmPassword: false
  });
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Full name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          error = 'Name must be less than 50 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          error = 'Name can only contain letters and spaces';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'mobile':
        if (!value.trim()) {
          error = 'Mobile number is required';
        } else if (!/^\d+$/.test(value.replace(/[\s\-\(\)]/g, ''))) {
          error = 'Mobile number can only contain digits';
        } else {
          const digits = value.replace(/[\s\-\(\)]/g, '');
          if (digits.length < 10) {
            error = 'Mobile number must be at least 10 digits';
          } else if (digits.length > 15) {
            error = 'Mobile number must be less than 15 digits';
          }
        }
        break;

      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        } else if (value.length > 50) {
          error = 'Password must be less than 50 characters';
        } else if (!/(?=.*[a-z])/.test(value)) {
          error = 'Password must contain at least one lowercase letter';
        } else if (!/(?=.*[A-Z])/.test(value)) {
          error = 'Password must contain at least one uppercase letter';
        } else if (!/(?=.*\d)/.test(value)) {
          error = 'Password must contain at least one number';
        }
        break;

      case 'confirmPassword':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors({
        ...fieldErrors,
        [name]: error
      });
    }

    // Real-time password match validation
    if (name === 'confirmPassword') {
      setPasswordMatch(value === formData.password || value === '');
    }
    if (name === 'password' && formData.confirmPassword) {
      setPasswordMatch(value === formData.confirmPassword);
      if (touched.confirmPassword) {
        const error = validateField('confirmPassword', formData.confirmPassword);
        setFieldErrors({
          ...fieldErrors,
          confirmPassword: error
        });
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    const error = validateField(name, value);
    setFieldErrors({
      ...fieldErrors,
      [name]: error
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    const errors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      mobile: validateField('mobile', formData.mobile),
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    };

    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      mobile: true,
      password: true,
      confirmPassword: true
    });

    // Set all errors
    setFieldErrors(errors);

    // Check if there are any errors
    if (Object.values(errors).some(error => error !== '')) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      // Add default role as 'user'
      const dataWithRole = {
        ...registerData,
        role: 'user'
      };
      const response = await registerUser(dataWithRole);
      
      // Check if response has the expected structure
      if (response && response.token) {
        // Store user and token
        login(response.user || { name: formData.name, email: formData.email }, response.token);
        navigate('/');
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error types
      if (error.response) {
        // Server responded with error
        const message = error.response.data?.message || error.response.data?.error || 'Registration failed';
        setError(message);
      } else if (error.request) {
        // Request made but no response
        setError('Cannot connect to server');
      } else {
        // Other errors
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Register</h1>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.name && fieldErrors.name ? 'input-error' : ''}
            />
            {touched.name && fieldErrors.name && (
              <span className="validation-error">{fieldErrors.name}</span>
            )}
            {touched.name && !fieldErrors.name && formData.name && (
              <span className="validation-success">✓ Valid name</span>
            )}
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.email && fieldErrors.email ? 'input-error' : ''}
            />
            {touched.email && fieldErrors.email && (
              <span className="validation-error">{fieldErrors.email}</span>
            )}
            {touched.email && !fieldErrors.email && formData.email && (
              <span className="validation-success">✓ Valid email</span>
            )}
          </div>

          <div className="form-group">
            <label>Mobile Number *</label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., 1234567890"
              className={touched.mobile && fieldErrors.mobile ? 'input-error' : ''}
            />
            {touched.mobile && fieldErrors.mobile && (
              <span className="validation-error">{fieldErrors.mobile}</span>
            )}
            {touched.mobile && !fieldErrors.mobile && formData.mobile && (
              <span className="validation-success">✓ Valid mobile number</span>
            )}
          </div>

          <div className="form-group">
            <label>Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.password && fieldErrors.password ? 'input-error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {touched.password && fieldErrors.password && (
              <span className="validation-error">{fieldErrors.password}</span>
            )}
            {touched.password && !fieldErrors.password && formData.password && (
              <span className="validation-success">✓ Strong password</span>
            )}
            {!touched.password && formData.password && (
              <div className="password-requirements">
                <small>Password must contain:</small>
                <small className={/(?=.*[a-z])/.test(formData.password) ? 'req-met' : ''}>• One lowercase letter</small>
                <small className={/(?=.*[A-Z])/.test(formData.password) ? 'req-met' : ''}>• One uppercase letter</small>
                <small className={/(?=.*\d)/.test(formData.password) ? 'req-met' : ''}>• One number</small>
                <small className={formData.password.length >= 6 ? 'req-met' : ''}>• At least 6 characters</small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Confirm Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.confirmPassword && fieldErrors.confirmPassword ? 'input-error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {touched.confirmPassword && fieldErrors.confirmPassword && (
              <span className="validation-error">{fieldErrors.confirmPassword}</span>
            )}
            {touched.confirmPassword && !fieldErrors.confirmPassword && formData.confirmPassword && (
              <span className="validation-success">✓ Passwords match</span>
            )}
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <p className="auth-link">
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
